import { useCallback, useMemo, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import moment, { type Moment } from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable } from "@/shared/utils/gridToPdfTable";
import { formatProxyDate } from "@/shared/utils/proxyDateTime";
import { useReturnedRolls } from "../hooks/useReturnedRolls";
import type {
	ReturnedRollRow,
	ReturnedRollsDateBasis,
} from "../models/ReturnedRolls";
import { ReturnedRollsSummary } from "./ReturnedRollsSummary";
import { ReturnedRollsToolbar } from "./ReturnedRollsToolbar";
import { STATUS_BY_VALUE, StatusSquare } from "./statusMeta";

const CENTER_CELL = { textAlign: "center" } as const;
const FLEX_CENTER_CELL = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;
const CENTER_HEADER = "ag-cell-center-header";

const toFixedOrEmpty = (value: unknown, decimals: number): string =>
	value == null ? "" : Number(value).toFixed(decimals);

// The retirement status, read from the stored state (Scrapped / Broken); none for a roll
// never retired. Whether it came back is its own column.
const StatusBadge = (p: ICellRendererParams<ReturnedRollRow>) => {
	const meta = p.value ? STATUS_BY_VALUE[p.value as string] : undefined;
	if (!meta) return null;
	return (
		<StatusSquare letter={meta.letter} color={meta.color} title={meta.label} />
	);
};

const returnedText = (p: { value: unknown }) =>
	p.value ? formatProxyDate(p.value as string) : "Awaiting return";

const buildColumnDefs = (): ColDef<ReturnedRollRow>[] => [
	{
		field: "Name",
		headerName: "Foundry Number",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RetirementStatus",
		headerName: "Status",
		width: 64,
		sortable: true,
		cellStyle: { ...FLEX_CENTER_CELL, padding: 0 },
		headerClass: CENTER_HEADER,
		cellRenderer: StatusBadge,
	},
	{
		field: "Diameter",
		headerName: "Diameter",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "ScrapDiameter",
		headerName: "Scrap Diameter",
		width: 140,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "ScrapWeight",
		headerName: "Scrap Weight",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Hardness",
		headerName: "Hardness",
		width: 110,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "MillSize",
		headerName: "Mill Size",
		width: 130,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "RollMaterial",
		headerName: "Roll Material",
		flex: 1,
		minWidth: 160,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "Cost",
		headerName: "Cost",
		width: 120,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => toFixedOrEmpty(p.value, 3),
	},
	{
		field: "ReceivedString",
		headerName: "Received",
		width: 150,
		sortable: true,
		sort: "asc",
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "RetiredString",
		headerName: "Retired",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => formatProxyDate(p.value),
	},
	{
		field: "ReturnedString",
		headerName: "Returned",
		width: 150,
		sortable: true,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: returnedText,
	},
];

// Opens as before: rolls returned in the range, scrapped and broken. The legacy URL called
// the first three scrap, broken and returned.
const DATE_BASES: readonly ReturnedRollsDateBasis[] = ["RETURNED", "RETIRED"];
const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	dateBy: urlParam.oneOf(DATE_BASES, "RETURNED"),
	scrap: urlParam.bool(true),
	broken: urlParam.bool(true),
	returned: urlParam.bool(true),
	awaiting: urlParam.bool(false),
};

export const ReturnedRollsReport = () => {
	const [
		{
			start,
			end,
			dateBy: dateBasis,
			scrap: scrapped,
			broken,
			returned,
			awaiting: awaitingReturn,
		},
		setFilters,
		{ resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<ReturnedRollRow> | null>(null);

	const columnDefs = useMemo(() => buildColumnDefs(), []);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useReturnedRolls({
		start: startStr,
		end: endStr,
		dateBasis,
		scrapped,
		broken,
		returned,
		awaitingReturn,
	});
	const rows = dataQuery.data ?? [];

	const handleExportToExcel = useCallback(() => {
		if (!gridApiRef.current) return;
		gridApiRef.current.exportDataAsExcel({
			fileName: `Returned_Rolls_${startStr}_${endStr}.xlsx`,
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "ReceivedString" || colId === "RetiredString") {
					return formatProxyDate(params.value);
				}
				if (colId === "ReturnedString") return returnedText(params);
				if (
					colId === "Diameter" ||
					colId === "ScrapDiameter" ||
					colId === "Cost"
				) {
					return toFixedOrEmpty(params.value, 3);
				}
				return params.value ?? "";
			},
		});
	}, [startStr, endStr]);

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportToPdf = () => {
		const api = gridApiRef.current;
		if (!api) return;
		const statuses = [scrapped && "Scrapped", broken && "Broken"].filter(
			Boolean,
		);
		const returns = [
			returned && "Returned",
			awaitingReturn && "Awaiting return",
		].filter(Boolean);
		const table = gridToPdfTable(api, {
			countLabel: "Total Rolls",
			aligns: {
				Diameter: "right",
				ScrapDiameter: "right",
				ScrapWeight: "right",
				Cost: "right",
			},
		});
		exportPdf(
			`Returned Rolls ${start.format("MM-DD-YYYY")} to ${end.format("MM-DD-YYYY")}.pdf`,
			() =>
				getReportPdf({
					title: "Returned Rolls",
					filters: [
						{ label: "Start Date", value: start.format("MM/DD/YYYY") },
						{ label: "Close Date", value: end.format("MM/DD/YYYY") },
						{
							label: "Date by",
							value: dateBasis === "RETIRED" ? "Retired" : "Returned",
						},
						{ label: "Status", value: statuses.join(", ") || "None" },
						{ label: "Return", value: returns.join(", ") || "None" },
					],
					tables: [table],
				}),
		);
	};

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Returned Rolls
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<ReturnedRollsToolbar
					onReset={() => resetValues()}
					canReset={!isDefault()}
					start={start}
					end={end}
					dateBasis={dateBasis}
					scrapped={scrapped}
					broken={broken}
					returned={returned}
					awaitingReturn={awaitingReturn}
					onDateBasisChange={(value) => setFilters({ dateBy: value })}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onScrappedChange={(value) => setFilters({ scrap: value })}
					onBrokenChange={(value) => setFilters({ broken: value })}
					onReturnedChange={(value) => setFilters({ returned: value })}
					onAwaitingReturnChange={(value) => setFilters({ awaiting: value })}
					onExportExcel={handleExportToExcel}
					onExportPdf={handleExportToPdf}
					canExportPdf={!dataQuery.isLoading && rows.length > 0}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			<ReturnedRollsSummary total={rows.length} />

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					overflow: "hidden",
				}}
			>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<ReturnedRollRow>
						columnDefs={columnDefs}
						rowData={rows}
						rowModelType="clientSide"
						defaultColDef={{ resizable: true }}
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
